// ============================================================================
// ProductionControlRoom_KeyboardShortcuts.tsx
// Modal de configuration des raccourcis clavier du deck
// v2.26 — passage à 12 slots + ajout rangée layout
// ============================================================================

import React from 'react';
import {
  DeckKeyMap,
  KeyBinding,
  DEFAULT_KEY_MAP,
  ROW_CONFIG,
} from '../../../dataTypes/liveStreamManagment/types_deck';
import {
  formatKeyBinding,
  findConflict,
} from '../../../dataTypes/liveStreamManagment/utils_deck';

// ─── Helper interne ───────────────────────────────────────────────────────────

const cloneKeyMap = (km: DeckKeyMap): DeckKeyMap => ({
  video:   [...km.video],
  preview: [...km.preview],
  cut:     [...km.cut],
  image:   [...km.image],
  sound:   [...km.sound],
  pip:     [...km.pip],
  jingle:  [...km.jingle],
  layout:  [...km.layout],
});

// ─── Props ────────────────────────────────────────────────────────────────────

interface KeyboardShortcutsModalProps {
  keyMap:              DeckKeyMap;
  onClose:             () => void;
  onChange:            (km: DeckKeyMap) => void;
  videoSlotLabels:     (string | null)[];
  imageSlotLabels:     string[];
  soundSlotLabels:     string[];
  pipSlotLabels:       string[];
  jingleSlotLabels:    string[];
  layoutSlotLabels:    string[];
}

// ─── SlotCell ─────────────────────────────────────────────────────────────────

interface SlotCellProps {
  row:          keyof DeckKeyMap;
  idx:          number;
  kb:           KeyBinding | null;
  slotLabel:    string | null;
  isCapturing:  boolean;
  isConflict:   boolean;
  isHovered:    boolean;
  modified:     boolean;
  onCapture:    () => void;
  onHover:      (on: boolean) => void;
  onClear:      () => void;
  onReset:      () => void;
}

const SlotCell: React.FC<SlotCellProps> = ({
  row, idx, kb, slotLabel,
  isCapturing, isConflict, isHovered, modified,
  onCapture, onHover, onClear, onReset,
}) => {
  const { border, bg, text, badge } = ROW_CONFIG[row];
  const isEmpty = !slotLabel;

  const borderColor = isConflict  ? '#f87171'
                    : isCapturing ? '#818cf8'
                    : isEmpty     ? 'rgba(255,255,255,0.05)'
                    : border;

  const bgColor     = isConflict  ? 'rgba(239,68,68,0.18)'
                    : isCapturing ? 'rgba(99,102,241,0.2)'
                    : isEmpty     ? 'rgba(255,255,255,0.02)'
                    : bg;

  return (
    <div
      className={isConflict ? 'kb-conflict-shake' : ''}
      onMouseEnter={() => { if (!isEmpty) onHover(true); }}
      onMouseLeave={() => onHover(false)}
      onClick={() => { if (!isEmpty) onCapture(); }}
      style={{
        border: `1.5px solid ${borderColor}`,
        borderRadius: 8,
        background: bgColor,
        padding: '8px 6px 6px',
        cursor: isEmpty ? 'not-allowed' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        position: 'relative',
        transition: 'border-color 0.12s, background 0.12s',
        boxShadow: isConflict  ? '0 0 14px rgba(239,68,68,0.4)'
                 : isCapturing ? '0 0 14px rgba(129,140,248,0.45)'
                 : 'none',
        minHeight: 80,
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <span style={{
          fontSize: 9, fontFamily: 'monospace', fontWeight: 700,
          color: isEmpty ? 'rgba(255,255,255,0.12)' : text, opacity: 0.6,
        }}>
          #{idx + 1}
        </span>
        {modified && !isEmpty && (
          <span
            style={{
              width: 5, height: 5, borderRadius: '50%',
              background: '#fb923c', boxShadow: '0 0 4px #fb923c',
              display: 'inline-block',
            }}
            title="Valeur modifiée — cliquez ↺ pour restaurer le défaut"
          />
        )}
      </div>

      <span style={{
        fontSize: 9,
        color: isEmpty     ? 'rgba(255,255,255,0.12)'
             : isConflict  ? '#fca5a5'
             : 'rgba(255,255,255,0.5)',
        textAlign: 'center', lineHeight: 1.3,
        overflow: 'hidden', display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
        wordBreak: 'break-word', width: '100%',
      }}>
        {isEmpty ? '—' : (slotLabel || '').replace(/^[📱🖥️📸🤳⧉🎬🖼🔊🌐🎞📐]\s*/, '')}
      </span>

      <div style={{
        marginTop: 'auto',
        padding: '3px 6px',
        borderRadius: 5,
        background: isConflict  ? 'rgba(239,68,68,0.3)'
                  : isCapturing ? 'rgba(129,140,248,0.3)'
                  : kb          ? badge
                  : 'rgba(255,255,255,0.04)',
        border: `1px solid ${
          isConflict  ? '#f87171'
          : isCapturing ? '#818cf8'
          : kb          ? border
          : 'rgba(255,255,255,0.07)'
        }`,
        fontSize: 10,
        fontFamily: 'monospace',
        fontWeight: 700,
        color: isConflict  ? '#fca5a5'
             : isCapturing ? '#c7d2fe'
             : kb          ? text
             : 'rgba(255,255,255,0.18)',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {isCapturing ? '…' : isConflict ? `⚠ ${formatKeyBinding(kb)}` : formatKeyBinding(kb)}
      </div>

      {isHovered && !isCapturing && !isEmpty && (
        <div style={{ position: 'absolute', top: 3, right: 3, display: 'flex', gap: 3 }}>
          {modified && (
            <button
              onClick={e => { e.stopPropagation(); onReset(); }}
              style={{
                width: 16, height: 16, borderRadius: 4,
                background: 'rgba(251,146,60,0.25)',
                border: '1px solid rgba(251,146,60,0.5)',
                color: '#fb923c', fontSize: 10, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 0, lineHeight: 1, fontWeight: 700,
              }}
              title={`Restaurer la valeur par défaut (${formatKeyBinding((DEFAULT_KEY_MAP[row] as (KeyBinding | null)[])[idx])})`}
            >↺</button>
          )}
          {kb && (
            <button
              onClick={e => { e.stopPropagation(); onClear(); }}
              style={{
                width: 16, height: 16, borderRadius: 4,
                background: 'rgba(239,68,68,0.22)',
                border: '1px solid rgba(239,68,68,0.45)',
                color: '#fca5a5', fontSize: 10, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 0, lineHeight: 1,
              }}
              title="Effacer ce raccourci"
            >×</button>
          )}
        </div>
      )}

      {isConflict && (
        <span
          style={{ position: 'absolute', top: 3, left: 3, fontSize: 11 }}
          title="Ce slot est en conflit"
        >⚠</span>
      )}
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  keyMap, onClose, onChange,
  videoSlotLabels, imageSlotLabels, soundSlotLabels, pipSlotLabels, jingleSlotLabels,
  layoutSlotLabels,
}) => {
  const [capturing,    setCapturing]    = React.useState<{ row: keyof DeckKeyMap; idx: number } | null>(null);
  const [conflictSlot, setConflictSlot] = React.useState<{ row: keyof DeckKeyMap; idx: number } | null>(null);
  const [conflictMsg,  setConflictMsg]  = React.useState<string | null>(null);
  const [hoveredSlot,  setHoveredSlot]  = React.useState<{ row: keyof DeckKeyMap; idx: number } | null>(null);

  const labelsMap: Record<keyof DeckKeyMap, (string | null)[]> = {
    video:   videoSlotLabels,
    preview: videoSlotLabels,
    cut:     ['CUT'],
    image:   imageSlotLabels,
    sound:   soundSlotLabels,
    pip:     pipSlotLabels,
    jingle:  jingleSlotLabels,
    layout:  layoutSlotLabels,
  };

  const previewGridRef = React.useRef<HTMLDivElement>(null);
  const cutCellRef     = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    const update = () => {
      if (!previewGridRef.current || !cutCellRef.current) return;
      const cells = previewGridRef.current.querySelectorAll(':scope > div');
      if (cells.length === 0) return;
      const cellW = (cells[0] as HTMLElement).getBoundingClientRect().width;
      cutCellRef.current.style.width = `${cellW}px`;
    };
    update();
    const ro = new ResizeObserver(update);
    if (previewGridRef.current) ro.observe(previewGridRef.current);
    return () => ro.disconnect();
  }, []);

  React.useEffect(() => {
    if (!conflictSlot) return;
    const t = setTimeout(() => { setConflictSlot(null); setConflictMsg(null); }, 2000);
    return () => clearTimeout(t);
  }, [conflictSlot]);

  React.useEffect(() => {
    if (!capturing) return;
    const handler = (e: KeyboardEvent) => {
      if (e.code === 'Escape') { e.preventDefault(); setCapturing(null); return; }
      if (['Control', 'Shift', 'Alt', 'Meta', 'CapsLock', 'Tab'].includes(e.key)) return;
      e.preventDefault();
      e.stopPropagation();

      const displayValue = e.key.length === 1 ? e.key : undefined;
      const nb: KeyBinding = {
        code:    e.code,
        display: displayValue,
        ctrl:    e.ctrlKey,
        shift:   e.shiftKey,
        alt:     e.altKey,
      };

      const c = findConflict(keyMap, nb, capturing.row, capturing.idx);
      if (c) {
        setConflictSlot(c);
        setConflictMsg(
          `⚠ Touche déjà utilisée par ${ROW_CONFIG[c.row].label.replace(/^[^ ]+ /, '')} #${c.idx + 1} — raccourci non assigné`
        );
        setCapturing(null);
        return;
      }

      const next = cloneKeyMap(keyMap);
      (next[capturing.row] as (KeyBinding | null)[])[capturing.idx] = nb;
      onChange(next);
      setCapturing(null);
      setConflictMsg(null);
      setConflictSlot(null);
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [capturing, keyMap, onChange]);

  const clearBinding = (row: keyof DeckKeyMap, idx: number) => {
    const next = cloneKeyMap(keyMap);
    (next[row] as (KeyBinding | null)[])[idx] = null;
    onChange(next);
    setConflictMsg(null);
    setConflictSlot(null);
  };

  const resetOneBinding = (row: keyof DeckKeyMap, idx: number) => {
    const defaultVal = (DEFAULT_KEY_MAP[row] as (KeyBinding | null)[])[idx] ?? null;
    if (defaultVal) {
      const c = findConflict(keyMap, defaultVal, row, idx);
      if (c) {
        setConflictSlot(c);
        setConflictMsg(
          `⚠ La valeur par défaut (${formatKeyBinding(defaultVal)}) est déjà utilisée par ${ROW_CONFIG[c.row].label.replace(/^[^ ]+ /, '')} #${c.idx + 1} — non restaurée`
        );
        return;
      }
    }
    const next = cloneKeyMap(keyMap);
    (next[row] as (KeyBinding | null)[])[idx] = defaultVal;
    onChange(next);
    setConflictMsg(null);
    setConflictSlot(null);
  };

  const isModified = (row: keyof DeckKeyMap, idx: number): boolean => {
    const current = (keyMap[row] as (KeyBinding | null)[])[idx];
    const def     = (DEFAULT_KEY_MAP[row] as (KeyBinding | null)[])[idx];
    if (!current && !def) return false;
    if (!current || !def) return true;
    return (
      current.code  !== def.code  ||
      current.ctrl  !== def.ctrl  ||
      current.shift !== def.shift ||
      current.alt   !== def.alt
    );
  };

  const startCapture = (row: keyof DeckKeyMap, idx: number, isCapturing: boolean) => {
    setConflictMsg(null);
    setConflictSlot(null);
    setCapturing(isCapturing ? null : { row, idx });
  };

  const renderSection = (
    row: keyof DeckKeyMap,
    cols: number,
    labels: (string | null)[],
    bindings: (KeyBinding | null)[],
  ) => {
    const { label } = ROW_CONFIG[row];
    return (
      <div key={row} style={{ marginBottom: 22 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: ROW_CONFIG[row].text,
          marginBottom: 10, textTransform: 'uppercase', letterSpacing: '1px',
        }}>
          {label}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8 }}>
          {bindings.map((kb, idx) => (
            <SlotCell
              key={idx}
              row={row} idx={idx} kb={kb}
              slotLabel={labels[idx] ?? null}
              isCapturing={capturing?.row === row && capturing?.idx === idx}
              isConflict={conflictSlot?.row === row && conflictSlot?.idx === idx}
              isHovered={hoveredSlot?.row === row && hoveredSlot?.idx === idx}
              modified={isModified(row, idx)}
              onCapture={() => startCapture(row, idx, capturing?.row === row && capturing?.idx === idx)}
              onHover={on => setHoveredSlot(on ? { row, idx } : null)}
              onClear={() => clearBinding(row, idx)}
              onReset={() => resetOneBinding(row, idx)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <style>{`
        @keyframes kbShake {
          0%,100% { transform: translateX(0); }
          20%      { transform: translateX(-5px); }
          40%      { transform: translateX(5px); }
          60%      { transform: translateX(-4px); }
          80%      { transform: translateX(3px); }
        }
        .kb-conflict-shake { animation: kbShake 0.35s ease; }
      `}</style>

      <div style={{
        background: '#0d1117', borderRadius: 16, width: '100%', maxWidth: 1100,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: '18px 20px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          flexShrink: 0,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#f0f6fc' }}>
              ⌨️ Raccourcis clavier du Deck
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>
              Cliquez sur un slot puis appuyez sur la touche souhaitée.<br />
              Combos Ctrl / Alt / Shift supportés. Pavé numérique distingué par le préfixe <strong style={{ color: 'rgba(255,255,255,0.5)' }}>⌨</strong>.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 16 }}>
            <button
              onClick={() => {
                onChange(DEFAULT_KEY_MAP);
                setConflictMsg(null);
                setConflictSlot(null);
                setCapturing(null);
              }}
              style={{
                fontSize: 12, padding: '6px 14px', borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
              }}
              title="Réinitialiser tous les raccourcis aux valeurs par défaut"
            >
              ↺ Tout réinitialiser
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.06)', border: 'none',
                color: 'rgba(255,255,255,0.6)', width: 30, height: 30,
                borderRadius: '50%', cursor: 'pointer', fontSize: 15, flexShrink: 0,
              }}
            >×</button>
          </div>
        </div>

        {/* ── Bannière conflit ── */}
        {conflictMsg && (
          <div style={{
            padding: '9px 20px',
            background: 'rgba(239,68,68,0.12)', borderBottom: '1px solid rgba(239,68,68,0.25)',
            fontSize: 12, color: '#fca5a5', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {conflictMsg}
            <button
              onClick={() => { setConflictMsg(null); setConflictSlot(null); }}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
            >×</button>
          </div>
        )}

        {/* ── Bannière capture ── */}
        {capturing && (
          <div style={{
            padding: '10px 20px',
            background: 'rgba(99,102,241,0.15)', borderBottom: '1px solid rgba(99,102,241,0.3)',
            fontSize: 13, color: '#a5b4fc', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#818cf8', boxShadow: '0 0 8px #818cf8', display: 'inline-block',
            }} />
            Appuyez sur une touche pour{' '}
            <strong>{ROW_CONFIG[capturing.row].label} #{capturing.idx + 1}</strong>
            <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.6 }}>Échap = annuler</span>
          </div>
        )}

        {/* ── Corps ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 20px' }}>

          {/* ── Sources vidéo ── */}
          {renderSection('video', 12, labelsMap.video, keyMap.video)}

          {/* ── Preview + CUT ── */}
          <div style={{ marginBottom: 22 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: ROW_CONFIG.preview.text,
              marginBottom: 10, textTransform: 'uppercase', letterSpacing: '1px',
            }}>
              {ROW_CONFIG.preview.label}
              <span style={{ marginLeft: 16, color: ROW_CONFIG.cut.text, opacity: 0.7 }}>
                + {ROW_CONFIG.cut.label}
              </span>
            </div>

            <div style={{ position: 'relative', paddingRight: 'calc(8.33% + 8px)' }}>
              <div
                ref={previewGridRef}
                style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 8 }}
              >
                {keyMap.preview.map((kb, idx) => (
                  <SlotCell
                    key={idx}
                    row="preview" idx={idx} kb={kb}
                    slotLabel={labelsMap.preview[idx] ?? null}
                    isCapturing={capturing?.row === 'preview' && capturing?.idx === idx}
                    isConflict={conflictSlot?.row === 'preview' && conflictSlot?.idx === idx}
                    isHovered={hoveredSlot?.row === 'preview' && hoveredSlot?.idx === idx}
                    modified={isModified('preview', idx)}
                    onCapture={() => startCapture('preview', idx, capturing?.row === 'preview' && capturing?.idx === idx)}
                    onHover={on => setHoveredSlot(on ? { row: 'preview', idx } : null)}
                    onClear={() => clearBinding('preview', idx)}
                    onReset={() => resetOneBinding('preview', idx)}
                  />
                ))}
              </div>

              <div
                ref={cutCellRef}
                style={{ position: 'absolute', bottom: 0, right: 0, height: '100%' }}
              >
                {(() => {
                  const kb         = keyMap.cut[0] ?? null;
                  const isCapt     = capturing?.row === 'cut' && capturing?.idx === 0;
                  const isConf     = conflictSlot?.row === 'cut' && conflictSlot?.idx === 0;
                  const isHov      = hoveredSlot?.row === 'cut' && hoveredSlot?.idx === 0;
                  const mod        = isModified('cut', 0);
                  const { border: cutBorder, bg: cutBg, text: cutText, badge: cutBadge } = ROW_CONFIG.cut;
                  const borderColor = isConf ? '#f87171' : isCapt ? '#818cf8' : cutBorder;
                  const bgColor     = isConf ? 'rgba(239,68,68,0.18)' : isCapt ? 'rgba(99,102,241,0.2)' : cutBg;

                  return (
                    <div
                      className={isConf ? 'kb-conflict-shake' : ''}
                      onMouseEnter={() => setHoveredSlot({ row: 'cut', idx: 0 })}
                      onMouseLeave={() => setHoveredSlot(null)}
                      onClick={() => startCapture('cut', 0, isCapt)}
                      style={{
                        width: '100%', height: '100%',
                        border: `1.5px solid ${borderColor}`, borderRadius: 8,
                        background: bgColor, padding: '8px 6px 6px',
                        cursor: 'pointer', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: 4,
                        position: 'relative', transition: 'border-color 0.12s, background 0.12s',
                        boxShadow: isConf ? '0 0 14px rgba(239,68,68,0.4)' : isCapt ? '0 0 14px rgba(129,140,248,0.45)' : 'none',
                        userSelect: 'none', boxSizing: 'border-box',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span style={{ fontSize: 9, fontFamily: 'monospace', fontWeight: 700, color: cutText, opacity: 0.6 }}>#1</span>
                        {mod && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fb923c', boxShadow: '0 0 4px #fb923c', display: 'inline-block' }} title="Valeur modifiée" />}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 800, fontFamily: 'monospace', color: isConf ? '#fca5a5' : cutText, textAlign: 'center', lineHeight: 1 }}>CUT</span>
                      <div style={{
                        marginTop: 'auto', padding: '3px 6px', borderRadius: 5,
                        background: isConf ? 'rgba(239,68,68,0.3)' : isCapt ? 'rgba(129,140,248,0.3)' : kb ? cutBadge : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${isConf ? '#f87171' : isCapt ? '#818cf8' : kb ? cutBorder : 'rgba(255,255,255,0.07)'}`,
                        fontSize: 10, fontFamily: 'monospace', fontWeight: 700,
                        color: isConf ? '#fca5a5' : isCapt ? '#c7d2fe' : kb ? cutText : 'rgba(255,255,255,0.18)',
                        whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {isCapt ? '…' : isConf ? `⚠ ${formatKeyBinding(kb)}` : formatKeyBinding(kb)}
                      </div>
                      {isHov && !isCapt && (
                        <div style={{ position: 'absolute', top: 3, right: 3, display: 'flex', gap: 3 }}>
                          {mod && <button onClick={e => { e.stopPropagation(); resetOneBinding('cut', 0); }} style={{ width:16,height:16,borderRadius:4,background:'rgba(251,146,60,0.25)',border:'1px solid rgba(251,146,60,0.5)',color:'#fb923c',fontSize:10,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0,lineHeight:1,fontWeight:700 }} title={`Restaurer (${formatKeyBinding((DEFAULT_KEY_MAP.cut as (KeyBinding|null)[])[0])})`}>↺</button>}
                          {kb && <button onClick={e => { e.stopPropagation(); clearBinding('cut', 0); }} style={{ width:16,height:16,borderRadius:4,background:'rgba(239,68,68,0.22)',border:'1px solid rgba(239,68,68,0.45)',color:'#fca5a5',fontSize:10,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0,lineHeight:1 }} title="Effacer">×</button>}
                        </div>
                      )}
                      {isConf && <span style={{ position:'absolute',top:3,left:3,fontSize:11 }} title="Conflit">⚠</span>}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* ── Images overlay ── */}
          {renderSection('image', 12, labelsMap.image, keyMap.image)}

          {/* ── Sons ── */}
          {renderSection('sound', 12, labelsMap.sound, keyMap.sound)}

          {/* ── Jingles vidéo ── */}
          {renderSection('jingle', 12, labelsMap.jingle, keyMap.jingle)}

          {/* ── Layouts ── */}
          {renderSection('layout', 12, labelsMap.layout, keyMap.layout)}

          {/* ── PiP vidéo ── */}
          {/* PiP : plus de bouton dans le deck, section masquée */}

          {/* ── Légende ── */}
          <div style={{
            marginTop: 6, padding: '10px 14px',
            background: 'rgba(255,255,255,0.03)', borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.05)',
            fontSize: 11, color: 'rgba(255,255,255,0.3)', lineHeight: 1.8,
          }}>
            <strong style={{ color: 'rgba(255,255,255,0.4)' }}>Touches affichées selon votre layout clavier :</strong><br />
            • Le label affiché correspond à ce qui est gravé sur votre clavier physique (AZERTY, QWERTY, etc.).<br />
            • Pavé numérique : préfixe <strong style={{ color: 'rgba(255,255,255,0.5)' }}>⌨</strong> — ex : <code style={{ color: 'rgba(255,255,255,0.45)' }}>⌨1</code> vs <code style={{ color: 'rgba(255,255,255,0.45)' }}>1</code>.<br />
            • <span style={{ color: '#fb923c' }}>●</span> = valeur modifiée par rapport au défaut &nbsp;|&nbsp; survol → boutons ↺ (restaurer) et × (effacer).
          </div>
        </div>
      </div>
    </div>
  );
};
